import { Injectable } from '@angular/core';
import { ApiService } from 'src/app/services/api/api.service';
import { CONFIG } from 'src/app/dataModels/config';
import { HelperService } from 'src/app/services/utils/helper.service';
import {
  CONSTANTS,
  COMPONENTS,
  ADDRESS_TYPE,
  PHONE_TYPE,
  EMAIL_TYPE,
  OPERATION,
  LOV_PROPERTIES,
  TABLE_SORT_ORDER,
  SORT_TYPE,
  TABLE_CELL_WIDTH,
} from 'src/app/dataModels/constants';
import { LoadingService } from '../shared/app-loading/loading.service';
import { CompAccessService } from 'src/app/services/compAccess/comp-access.service';
import { ResourceManagerService } from 'src/app/services/resourceManager/resource-manager.service';
import { LOV_KEYS } from 'src/app/dataModels/lovs';
import { TelephonePipe } from 'src/app/pipes/telephone.pipe';
import { lastValueFrom } from 'rxjs';

/**
 * Facility Service
 */
@Injectable({
  providedIn: 'root',
})
export class FacilityService {
  /**
   * Profile List to be excluded from Facility Users
   */
  excludedProfileList = ['VFC ADMIN', 'SUPER ADMIN', 'TRAINER', 'QA', 'CDS USER', 'GENERAL'];

  /**
   * Headers for Search Facility
   */
  searchFacilityHeaders = [
    {
      text: 'Facility Name',
      field: 'facilityName',
      cssClass: TABLE_CELL_WIDTH.MEDIUM1,
      cellTemplate: 'LINK',
      sort: true,
      sortOrder: TABLE_SORT_ORDER.ASC,
      isFrozen: true,
    },
    {
      text: 'Facility Id',
      field: 'facilityId',
      cssClass: TABLE_CELL_WIDTH.SMALL,
      sortType: 'number',
    },
    {
      text: 'Facility Status',
      field: 'facilityStatus',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'VFC Pin',
      field: 'vfcPin',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'VFC Status',
      field: 'vfcStatus',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'COVID-19 Pin Status',
      field: 'covidStatus',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'Address',
      field: 'address',
      cssClass: TABLE_CELL_WIDTH.MEDIUM1,
    },
    {
      text: 'Info',
      field: '',
      cellTemplate: 'DYNAMIC_POPOVER',
      id: 'facilityInfoTemplate',
      cssClass: TABLE_CELL_WIDTH.SMALLEST,
      noSort: true,
      isFrozen: true,
    },
  ];

  /**
   * Headers for Interface profile comments
   */
  intfcCommentsHeaders = [
    {
      text: 'User',
      field: 'userCreated',
      cssClass: TABLE_CELL_WIDTH.MEDIUM,
    },
    {
      text: 'Date Created',
      field: 'dateCreated',
      cssClass: TABLE_CELL_WIDTH.MEDIUM,
      sortType: 'date',
      sort: true,
      sortOrder: TABLE_SORT_ORDER.DESC,
    },
    {
      text: 'Comments',
      field: 'comments',
      cssClass: TABLE_CELL_WIDTH.LARGE,
    },
  ];

  errorDescr = 'Error';
  /**
   * Headers for Facility users
   */
  facilityUsersHeaders = [
    {
      text: 'User Name',
      field: 'userName',
      cssClass: TABLE_CELL_WIDTH.SMALL,
      cellTemplate: 'LINK',
    },
    {
      text: 'Last Name',
      field: 'lastName',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'First Name',
      field: 'firstName',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'VFC Pin',
      field: 'vfcPin',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'Profile Type',
      field: 'profileType',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'Profile Name',
      field: 'profileName',
      cssClass: TABLE_CELL_WIDTH.SMALL,
    },
    {
      text: 'Status',
      field: 'status',
      cssClass: TABLE_CELL_WIDTH.SMALL,
      sort: true,
      sortOrder: TABLE_SORT_ORDER.ASC,
    },
  ];

  /**
   * Headers for Audit Note
   */
  getNoteHeader(isUpdateAllowed = true) {
    const noteHeaders = [] as any;
    noteHeaders.push(
      {
        text: 'Audit By',
        field: 'noteBy',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Audit Type',
        field: 'noteTypeDescr',
        cssClass: TABLE_CELL_WIDTH.SMALLER,
      },
      {
        text: 'Audit Date',
        field: 'noteDate',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
      }
    );
    if (isUpdateAllowed) {
      noteHeaders.push({
        text: 'Audit Visible To',
        field: 'visibilityToList',
        cellTemplate: 'MULTISELECT',
        cssClass: TABLE_CELL_WIDTH.MEDIUM1,
      });
    } else {
      noteHeaders.push({
        text: 'Audit Visible To',
        field: 'visibilityToListDescr',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      });
    }
    noteHeaders.push(
      {
        text: 'VFC Pin',
        field: 'vfcPin',
        cssClass: TABLE_CELL_WIDTH.SMALLER,
      },
      {
        text: 'Comment',
        field: 'comments',
        cssClass: TABLE_CELL_WIDTH.MEDIUM,
      },
      {
        text: 'Created By',
        field: 'userCreated',
        cssClass: TABLE_CELL_WIDTH.SMALL,
      },
      {
        text: 'Date Created',
        field: 'dateCreated',
        cssClass: TABLE_CELL_WIDTH.SMALL,
        sortType: SORT_TYPE.DATE,
        sort: true,
        sortOrder: TABLE_SORT_ORDER.DESC,
      }
    );
    if (isUpdateAllowed) {
      noteHeaders.push({
        text: 'Update',
        field: '',
        cssClass: TABLE_CELL_WIDTH.SMALLER,
        cellTemplate: 'UPDATE',
        noSort: true,
      });
    }

    return noteHeaders;
  }

  /**
   * Constructor to inject dependency
   * @param apiService instance of APIService
   * @param helperService instance of helperService
   * @param loadingService instance of loadingService
   * @param compAccessService instance of CompAccessService
   * @param resourceManagerService instance of ResourceManagerService
   * @param telephonePipe instance of Telephone pipe
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService,
    private loadingService: LoadingService,
    private compAccessService: CompAccessService,
    private resourceManagerService: ResourceManagerService,
    private telephonePipe: TelephonePipe
  ) { }

  /**
   * Get Facility Type
   */
  async getFacilityType(facilityId?) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_FACILITY_TYPE,
          params: {
            facilityId,
          },
        },
      ],
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));
  }

  /**
   * Search Facility
   * @param params search facility form page params
   * @param hideLoader indicate if loader has to be hidden
   * @param searchByHeirarchyFilter indicate is facility has to be search by heirarchy filter
   */
  async searchFacility(params: any, hideLoader?: boolean, searchByHeirarchyFilter = true) {
    const request = {
      objects: [],
    };

    const searchParams = {} as any;

    if (searchByHeirarchyFilter) {
      searchParams.facilityHrchy = CONSTANTS.FACILITY_HRCHY_AF;
      searchParams.facilityHrchyFilter = CONSTANTS.FACILITY_HRCHY_AF;
    }

    if (!this.helperService.IsNullOrEmpty(params.facilityId)) {
      searchParams.facilityId = params.facilityId?.trim();
    } else if (!this.helperService.IsNullOrEmpty(params.vfcPin)) {
      searchParams.vfcPin = params.vfcPin?.trim();
    } else if (!this.helperService.IsNullOrEmpty(params.taxId)) {
      searchParams.facilityTaxId = params.taxId.replace(/-/g, '');
    } else if (!this.helperService.IsNullOrEmpty(params.webSvcAccountId)) {
      searchParams.webSvcAccountId = params.webSvcAccountId?.trim();
    } else if (!this.helperService.IsNullOrEmpty(params.facilityName)) {
      searchParams.facilityName = params.facilityName?.trim();
    } else {
      if (!this.helperService.IsNullOrEmpty(params.system)) {
        searchParams.systemList = [params.system];
        searchParams.facilityHrchy = CONSTANTS.FACILITY_HRCHY_SYSTEM;
      }

      if (!this.helperService.IsNullOrEmpty(params.group)) {
        searchParams.groupList = [];
        for (const grp of params.group) {
          searchParams.groupList.push(grp.columnValue);
        }
        if (searchParams.groupList.length > 0) {
          searchParams.systemList = [];
          searchParams.facilityHrchy = CONSTANTS.FACILITY_HRCHY_GROUP;
        }
      }

      if (!this.helperService.IsNullOrEmpty(params.county)) {
        searchParams.countyList = [];
        for (const county of params.county) {
          searchParams.countyList.push(county.countyCode);
        }
      }

      if (!this.helperService.IsNullOrEmpty(params.countyList)) {
        searchParams.countyList = [];
        for (const county of params.countyList) {
          searchParams.countyList.push(county.countyCode);
        }
      }

      if (!this.helperService.IsNullOrEmpty(params.facilityType)) {
        searchParams.facilityTypeList = [];
        for (const facilityType of params.facilityType) {
          searchParams.facilityTypeList.push(facilityType.columnValue);
        }
      }

      if (!this.helperService.IsNullOrEmpty(params.facilityTypeList)) {
        searchParams.facilityTypeList = [];
        for (const facilityType of params.facilityTypeList) {
          searchParams.facilityTypeList.push(facilityType.columnValue);
        }
      }
    } // end of else
    if (!this.helperService.IsNullOrEmpty(params.userName)) {
      searchParams.userName = params.userName;
    }
    request.objects.push({
      name: OPERATION.FACILITY.SEARCH_FACILITY,
      params: searchParams,
    });
    //if (!hideLoader) {
      //this.loadingService.displayLoader('Searching Facilities');
    //}
    const results = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request, true));

    //this.loadingService.hideLoader();

    let facilitySearchResults = [];

    if (results && (results.status === CONSTANTS.SUCCESS || results.status === CONSTANTS.TRN_WARNING)) {
      if (results.objects && results.objects.length > 0 && results.objects[0].json) {
        facilitySearchResults = this.transformSearchFacilityResults(results.objects[0].json);
      }
    }
    return facilitySearchResults;
  }

  /**
   * format address into single line
   * @param results search facility results
   */
  private transformSearchFacilityResults(results) {
    const searchResults = [];
    for (const data of results) {
      data.facility.facilityType = data.facilityType.facilityType;
      data.facility.facilityTypeName = data.facilityType.facilityTypeName;
      data.facility.address = this.helperService.formatAddress(data.address);
      data.facility.county = data.address.county;
      searchResults.push(data.facility);
    }

    return searchResults;
  }

  /**
   * get facility Interfce profile
   */
  async getFacilityInterfaceProfile(facilityId, facilityIdSeq?) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_INTERFACE_PROFILE,
          params: {
            facilityId,
            facilityIdSeq,
            facilityHrchy: CONSTANTS.FACILITY_HRCHY_AF,
          },
        },
      ],
    };

    let interfaceProfileInfo = { noData: false } as any;

    const results = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    if (results && (results.status === CONSTANTS.SUCCESS || results.status === CONSTANTS.TRN_WARNING)) {
      if (
        results.objects &&
        results.objects.length > 0 &&
        results.objects[0].json &&
        results.objects[0].json.length > 0
      ) {
        interfaceProfileInfo = this.parseInterfaceProfileInfo(results.objects[0].json[0]);
      } else {
        interfaceProfileInfo.noData = true;
      }
      if (
        results.objects &&
        results.objects.length > 0 &&
        results.objects[0].json &&
        results.objects[0].json.note &&
        results.objects[0].json.note.json &&
        results.objects[0].json.note.json.length > 0
      ) {
        interfaceProfileInfo.comments = results.objects[0].json.note.json.filter((note) => {
          return !this.helperService.IsNullOrEmpty(note.intfcProfileMapSeq);
        });

        for (const intfcComment of interfaceProfileInfo.comments) {
          intfcComment.comments = this.helperService.decodeJsonString(intfcComment.comments);
        }
      }
    }

    return interfaceProfileInfo;
  }

  /**
   * format data for interface profile
   */
  private parseInterfaceProfileInfo(interfaceProfile) {
    let intfcProfileInfo = {} as any;
    try {
      if (interfaceProfile) {
        const intfcProfileMap = interfaceProfile.intfcProfileMap as any;
        if (intfcProfileMap && intfcProfileMap.intfcProfileMapSeq) {
          intfcProfileInfo.action = 'UPDATE_INTERFACE_PROFILE';
          intfcProfileInfo.intfcProfileMapSeq = intfcProfileMap.intfcProfileMapSeq;
          intfcProfileInfo.intfcEntityIdSeq = intfcProfileMap.intfcEntityIdSeq;
          intfcProfileInfo.intfcFormId = intfcProfileMap.intfcFormId;
          intfcProfileInfo.intfcType = intfcProfileMap.intfcType;
          intfcProfileInfo.intfcAccessType = intfcProfileMap.intfcAccessType;
          intfcProfileInfo.intfcEngine = intfcProfileMap.intfcEngine;
          intfcProfileInfo.fileVersion = intfcProfileMap.fileVersion;
          intfcProfileInfo.fileType = intfcProfileMap.fileType;
          intfcProfileInfo.status = intfcProfileMap.status;
          intfcProfileInfo.originalIntfcDate = intfcProfileMap.originalIntfcDate;
          intfcProfileInfo.intfcEngineImplmenDate = intfcProfileMap.intfcEngineImplmenDate;
          intfcProfileInfo.userCreated = intfcProfileMap.userCreated;

          // Vendor
          const vendor = interfaceProfile.vendor;
          if (vendor) {
            intfcProfileInfo.vendorIdSeq = vendor.vendorIdSeq;
            intfcProfileInfo.vendorName = vendor.vendorName;
            intfcProfileInfo.vendorSoftware = vendor.vendorSoftware;
            intfcProfileInfo.vendorStatus = vendor.vendorStatus;
          } // if (vendor)

          // Entity
          const intfcEntity = interfaceProfile.intfcEntity;
          if (intfcEntity) {
            intfcProfileInfo.intfcEntityIdSeq = intfcEntity.intfcEntityIdSeq;
            intfcProfileInfo.intfcEntityName = intfcEntity.intfcEntityName;
            intfcProfileInfo.intfcEntityType = intfcEntity.intfcEntityType;
            intfcProfileInfo.intfcEntitySubType = intfcEntity.intfcEntitySubType;
            intfcProfileInfo.intfcEntityStatus = intfcEntity.intfcEntityStatus;
          } // if (intfcEntity)

          // Entity Interface Profile
          const entityIntfcProfile = interfaceProfile.entityIntfcProfile;
          if (entityIntfcProfile) {
            intfcProfileInfo.entityIntfcProfile = {};
            intfcProfileInfo.entityIntfcProfile.webSvcAccountId = entityIntfcProfile.webSvcAccountId;
            intfcProfileInfo.entityIntfcProfile.webSvcAccountIdType = entityIntfcProfile.webSvcAccountIdType;
            intfcProfileInfo.entityIntfcProfile.webSvcAccountIdStatus = entityIntfcProfile.webSvcAccountIdStatus;
            intfcProfileInfo.entityIntfcProfile.sftpAccountId = entityIntfcProfile.sftpAccountId;
            intfcProfileInfo.entityIntfcProfile.sftpAccountIdStatus = entityIntfcProfile.sftpAccountIdStatus;
          } // if (entityIntfcProfile)

          // Facility Interface Profile
          const facilityIntfcProfile = interfaceProfile.facilityIntfcProfile;
          if (facilityIntfcProfile) {
            intfcProfileInfo.facilityIntfcProfile = {};
            intfcProfileInfo.facilityIntfcProfile.webSvcAccountId = facilityIntfcProfile.webSvcAccountId;
            intfcProfileInfo.facilityIntfcProfile.webSvcAccountIdType = facilityIntfcProfile.webSvcAccountIdType;
            intfcProfileInfo.facilityIntfcProfile.webSvcAccountIdStatus = facilityIntfcProfile.webSvcAccountIdStatus;
            intfcProfileInfo.facilityIntfcProfile.sftpAccountId = facilityIntfcProfile.sftpAccountId;
            intfcProfileInfo.facilityIntfcProfile.sftpAccountIdStatus = facilityIntfcProfile.sftpAccountIdStatus;
          } // if (facilityIntfcProfile)

          // Active Interface Profile
          if (intfcProfileInfo.intfcEntityIdSeq) {
            intfcProfileInfo.webSvcAccountId = intfcProfileInfo.entityIntfcProfile.webSvcAccountId;
            intfcProfileInfo.webSvcAccountIdType = intfcProfileInfo.entityIntfcProfile.webSvcAccountIdType;
            intfcProfileInfo.webSvcAccountIdStatus = intfcProfileInfo.entityIntfcProfile.webSvcAccountIdStatus;
            intfcProfileInfo.sftpAccountId = intfcProfileInfo.entityIntfcProfile.sftpAccountId;
            intfcProfileInfo.sftpAccountIdStatus = intfcProfileInfo.entityIntfcProfile.sftpAccountIdStatus;
          } else {
            intfcProfileInfo.webSvcAccountId = intfcProfileInfo.facilityIntfcProfile.webSvcAccountId;
            intfcProfileInfo.webSvcAccountIdType = intfcProfileInfo.facilityIntfcProfile.webSvcAccountIdType;
            intfcProfileInfo.webSvcAccountIdStatus = intfcProfileInfo.facilityIntfcProfile.webSvcAccountIdStatus;
            intfcProfileInfo.sftpAccountId = intfcProfileInfo.facilityIntfcProfile.sftpAccountId;
            intfcProfileInfo.sftpAccountIdStatus = intfcProfileInfo.facilityIntfcProfile.sftpAccountIdStatus;
          }
        } // if (intfcProfileMap && intfcProfileMap.intfcProfileMapSeq)
        intfcProfileInfo.noData = false;
      } // if (interfaceProfile)
    } catch (err) {
      intfcProfileInfo = { noData: true };
    }
    return intfcProfileInfo;
  }

  /**
   * Check all components View Access for Facility
   */
  async checkAllViewFacilityCompAccess() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.VIEW_FACILITY,
      COMPONENTS.FACILITY.VIEW_CONTACT,
      COMPONENTS.FACILITY.VIEW_ADDRESS,
      COMPONENTS.FACILITY.VIEW_AUDIT_REVIEW,
      COMPONENTS.FACILITY.VIEW_INTERFACE_PROFILE,
      COMPONENTS.FACILITY.VIEW_VFC,
      COMPONENTS.FACILITY.VIEW_FACILITY_USERS,
      COMPONENTS.COVID.VACCINE_ENROLLMENT,
    ]);
  }

  /**
   * check component access to update or view facility general info
   */
  async checkCompAccessFacilityGeneralInfo() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.ADD_FACILITY,
      COMPONENTS.FACILITY.UPDATE_FACILITY,
      COMPONENTS.FACILITY.VIEW_FACILITY,
    ]);
  }

  /**
   * check component access to add, update or view facility contact info
   */
  async checkCompAccessFacilityContact() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.ADD_CONTACT,
      COMPONENTS.FACILITY.UPDATE_CONTACT,
      COMPONENTS.FACILITY.VIEW_CONTACT,
    ]);
  }

  /**
   * check component access to add, update or view facility address
   */
  async checkCompAccessFacilityAddress() {
    return await this.compAccessService.checkAccess([COMPONENTS.FACILITY.UPDATE_ADDRESS, COMPONENTS.FACILITY.VIEW_ADDRESS]);
  }

  /**
   * check component access to add, update or view facility interface profile
   */
  async checkCompAccessFacilityInteraceProfile() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.ADD_INTERFACE_PROFILE,
      COMPONENTS.FACILITY.UPDATE_INTERFACE_PROFILE,
      COMPONENTS.FACILITY.VIEW_INTERFACE_PROFILE,
    ]);
  }

  /**
   * check component access to add, update or view facility audit note
   */
  async checkCompAccessFacilityAuditNote() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.ADD_AUDIT_REVIEW,
      COMPONENTS.FACILITY.UPDATE_AUDIT_REVIEW,
      COMPONENTS.FACILITY.VIEW_AUDIT_REVIEW,
    ]);
  }

  /**
   * check component access to add, update or view facility VFC
   */
  async checkCompAccessFacilityVFC() {
    return await this.compAccessService.checkAccess([
      COMPONENTS.FACILITY.ADD_VFC,
      COMPONENTS.FACILITY.UPDATE_VFC,
      COMPONENTS.FACILITY.VIEW_VFC,
    ]);
  }

  /**
   * Checks if the entered facility Name already exists
   * @param facilityName facility Name entered
   * @param facilityHrchy heirarchy of the facility AF, SYSTEM or GROUP
   */
  async checkFacilityName(facilityName, facilityHrchy) {
    const request = { name: facilityName, entity: facilityHrchy };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MS_CHECK_NAME, request));

    const facilityNameResult = {
      isNameAvailable: '',
      isNameDup: '',
      isError: '',
    };

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.nameAvailable === 'Y') {
        facilityNameResult.isNameAvailable = 'Y';
      } else if (result.nameAvailable === 'N') {
        facilityNameResult.isNameDup = 'Y';
      } else {
        facilityNameResult.isError = 'Y';
      }
    } else {
      facilityNameResult.isError = 'Y';
    }

    return facilityNameResult;
  }

  /**
   * Makes api call to Create Facility
   * @param generalFacilityInfo Facility's general accordion information
   * @param contactInfo Facility's contact accordion information
   * @param addressInfo Facility's address accordion information
   */
  async createFacility(generalFacilityInfo, contactInfo, addressInfo, phyAddressInfo) {
    this.loadingService.displayLoader(`Creating Facility ${generalFacilityInfo.facilityName.toUpperCase()}`);

    let result;

    try {
      const request = {
        action: OPERATION.FACILITY.CREATE_FACILITY,
        facilityName: generalFacilityInfo.facilityName.toUpperCase(),
        facilityHrchy: CONSTANTS.FACILITY_HRCHY_AF,
        facilityStatus: generalFacilityInfo.status,
        facilityTaxId: generalFacilityInfo.taxId,
        facilityNpi: generalFacilityInfo.npi,
        facilitySpecializationList: this.getMultipleColumnValueList(generalFacilityInfo.specialization),
        facilityType: {
          action: CONSTANTS.ADD,
          facilityType: generalFacilityInfo.facilityType,
          facilitySubtypeList: [generalFacilityInfo.facilitySubtype],
        },
        addressList: [
        {
          action: CONSTANTS.ADD,
          addressType: ADDRESS_TYPE.BUSINESS,
          addressLine1: addressInfo.addressLine1,
          addressLine2: addressInfo.addressLine2,
          city: addressInfo.city,
          state: addressInfo.state,
          zip: addressInfo.zip,
          countyCode: addressInfo.countyCode ? addressInfo.countyCode : '0000',
          municipalityCode: addressInfo.municipalityCode ? addressInfo.municipalityCode : '0000',
        },
        {
          action: CONSTANTS.ADD,
          addressType: ADDRESS_TYPE.PHYSICAL,
          addressLine1: phyAddressInfo.addressLine1,
          addressLine2: phyAddressInfo.addressLine2,
          city: phyAddressInfo.city,
          state: phyAddressInfo.state,
          zip: phyAddressInfo.zip,
          countyCode: phyAddressInfo.countyCode ? phyAddressInfo.countyCode : '0000',
          municipalityCode: phyAddressInfo.municipalityCode ? phyAddressInfo.municipalityCode : '0000',
        }
        ],
        phoneList: this.formPhoneAndFaxRequest(generalFacilityInfo, CONSTANTS.ADD),
        email: {
          action: CONSTANTS.ADD,
          emailType: EMAIL_TYPE.WORK,
          emailAddress: generalFacilityInfo.email,
        },
        contactList: this.formContactListRequest(contactInfo, CONSTANTS.ADD),
        groupSeq: generalFacilityInfo.group,
        systemSeq: generalFacilityInfo.system,
        inNJVSS: "",
      };

      if (generalFacilityInfo.audiologicalFacility === CONSTANTS.YES) {
        request.facilityType.facilitySubtypeList.push('8');
      }

      result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_MS_FACILITY, request));
    } catch (e) { }

    this.loadingService.hideLoader();

    return result;
  }

  /**
   * Returns the list of the columnValues of selected list
   * @param selectedList selected multiselect list
   */
  getMultipleColumnValueList(selectedList) {
    let selectedValueList = [];
    if (selectedList && selectedList.length > 0) {
      selectedValueList = selectedList.map((x) => x.columnValue);
    }
    return selectedValueList;
  }

  /**
   * Forms phoneList array for Facility's general section
   * @param phoneObj generalFacilityInfo object
   */
  formPhoneAndFaxRequest(phoneObj, action) {
    const phoneList = [];
    if (!this.helperService.IsNullOrEmpty(phoneObj.phone)) {
      phoneList.push({
        action,
        phoneType: PHONE_TYPE.WORK,
        phoneNumber: phoneObj.phone.replace('(', '').replace(')', '').replace('-', ''),
        phoneExtn: phoneObj.phoneExtn ? phoneObj.phoneExtn : '',
      });
    }

    if (!this.helperService.IsNullOrEmpty(phoneObj.fax)) {
      phoneList.push({
        action,
        phoneType: PHONE_TYPE.FAX,
        phoneNumber: phoneObj.fax.replace('(', '').replace(')', '').replace('-', ''),
      });
    }

    return phoneList;
  }

  /**
   * forms request for contact information
   * @param contactListInfo contact List
   * @param mode mode ADD/UPDATE
   */
  formContactListRequest(contactListInfo, mode) {
    const addedContactList = [];
    if (contactListInfo) {
      for (const contact of contactListInfo) {
        const newContact = {} as any;
        newContact.action = mode;
        if (mode === CONSTANTS.UPDATE) {
          newContact.contactIdSeq = contact.contactSeq;
        }
        (newContact.firstName = contact.firstName.toUpperCase()),
          (newContact.lastName = contact.lastName.toUpperCase()),
          (newContact.middleName = contact.middleName.toUpperCase()),
          (newContact.contactType = contact.contactType),
          (newContact.email = {
            action: mode,
            emailSeq: '',
            emailType: EMAIL_TYPE.WORK,
            emailAddress: contact.email,
          } as any);
        newContact.phoneList = this.formPhoneAndFaxRequest(contact, mode);
        if (mode === CONSTANTS.UPDATE) {
          newContact.email.emailSeq = contact.emailSeq;
          for (const phone of newContact.phoneList) {
            if (phone.phoneType === PHONE_TYPE.WORK) {
              phone.phoneSeq = contact.phoneSeq;
            } else {
              phone.phoneSeq = contact.faxSeq;
            }
          }
        }
        addedContactList.push(newContact);
      }
    }
    return addedContactList;
  }

  /**
   * Retrieves General Accordion information
   * @param facilityId Facility ID for whom information has to be retrieved
   * @param facilityIdSeq Seq Number of the Facility Id
   */
  async getFacilityInfo(facilityId, facilityIdSeq) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_FACILITY_GENERAL_INFO,
          params: {
            facilityId,
            facilityIdSeq,
            facilityHrchy: CONSTANTS.FACILITY_HRCHY_AF,
          },
        },
      ],
    };

    const contactRequest = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_PHONE,
          params: {
            facilityId,
          },
        },
        {
          name: OPERATION.GLOBAL.GET_EMAIL,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    const facilityInfoResult = {} as any;

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      facilityInfoResult.status = CONSTANTS.SUCCESS;
      if (result.objects && result.objects.length > 0 && result.objects[0].json) {
        if (result.objects[0].json && result.objects[0].json.length > 0) {
          facilityInfoResult.facilityList = result.objects[0];
        }
      }
    } else {
      facilityInfoResult.status = CONSTANTS.TRN_ERROR;
    }

    const contactResult = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, contactRequest));
    if (contactResult.status === CONSTANTS.SUCCESS || contactResult.status === CONSTANTS.TRN_WARNING) {
      facilityInfoResult.status = CONSTANTS.SUCCESS;
      const response = {} as any;
      if (contactResult.objects) {
        for (const resultObj of contactResult.objects) {
          if (resultObj.name) {
            response[resultObj.name] = resultObj.json;
          }
        }
      }
     
      if (response[OPERATION.GLOBAL.GET_PHONE]) {
        facilityInfoResult.phoneList = response[OPERATION.GLOBAL.GET_PHONE];
      }
      if (response[OPERATION.GLOBAL.GET_EMAIL]) {
        facilityInfoResult.emailList = response[OPERATION.GLOBAL.GET_EMAIL];
      }
    }
    else {
      facilityInfoResult.status = CONSTANTS.TRN_ERROR;
    }

    return facilityInfoResult;
  }

  /**
   * Update Facility's general information
   * @param generalInfo Facility general information which needs to be updated
   */
  async manageFacilityGeneralInfo(facilityId, facilityIdSeq, origGeneralFacilityInfo, generalFacilityInfo, comment) {
    const request = {
      action: OPERATION.FACILITY.UPDATE_FACILITY,
      facilityId,
      facilityIdSeq,
      facilityName: generalFacilityInfo.facilityName,
      facilityHrchy: CONSTANTS.FACILITY_HRCHY_AF,
      facilityStatus: generalFacilityInfo.status,
      facilityTaxId: generalFacilityInfo.taxId,
      comment: comment,
      facilitySpecializationList: this.getMultipleColumnValueList(generalFacilityInfo.specialization),
      facilityType: {
        action: CONSTANTS.UPDATE,
        facilityType: generalFacilityInfo.facilityType,
        facilitySubtypeList: [generalFacilityInfo.facilitySubtype],
      },
      phoneList: this.compareAndFormPhoneAndFaxRequest(origGeneralFacilityInfo, generalFacilityInfo),
      email: {
        action: CONSTANTS.UPDATE,
        emailType: EMAIL_TYPE.WORK,
        emailAddress: generalFacilityInfo.email,
        emailSeq: origGeneralFacilityInfo.emailSeq,
      },
      groupSeq: generalFacilityInfo.group,
      systemSeq: generalFacilityInfo.system,
      inNJVSS: this.helperService.getCheckboxValue(origGeneralFacilityInfo.inNJVSS),
    };

    if (generalFacilityInfo.audiologicalFacility === CONSTANTS.YES) {
      request.facilityType.facilitySubtypeList.push('8');
    }

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_MS_FACILITY, request));

    return result;
  }

  /**
   * Compares phone and fax and return arraylist containing phone and fax with appropriate action
   * @param oldData Object having phone and fax before update
   * @param newData Updated object having phone and fax
   */
  compareAndFormPhoneAndFaxRequest(oldData, newData) {
    const phoneList = [];
    if (!this.helperService.IsNullOrEmpty(newData.phone)) {
      phoneList.push({
        action: !this.helperService.IsNullOrEmpty(oldData.phoneSeq) ? CONSTANTS.UPDATE : CONSTANTS.ADD,
        phoneType: PHONE_TYPE.WORK,
        phoneNumber: newData.phone.replace('(', '').replace(')', '').replace('-', ''),
        phoneExtn: newData.phoneExtn,
        phoneSeq: oldData.phoneSeq,
        primaryInd: 'Y',
      });
    }

    const faxDataObj = {} as any;

    if (this.helperService.IsNullOrEmpty(oldData.fax) && !this.helperService.IsNullOrEmpty(newData.fax)) {
      faxDataObj.action = CONSTANTS.ADD;
      faxDataObj.phoneNumber = newData.fax.replace('(', '').replace(')', '').replace('-', '');
    } else if (!this.helperService.IsNullOrEmpty(oldData.fax) && !this.helperService.IsNullOrEmpty(newData.fax)) {
      faxDataObj.action = CONSTANTS.UPDATE;
      faxDataObj.phoneNumber = newData.fax.replace('(', '').replace(')', '').replace('-', '');
      faxDataObj.phoneSeq = oldData.faxSeq;
    } else if (!this.helperService.IsNullOrEmpty(oldData.fax) && this.helperService.IsNullOrEmpty(newData.fax)) {
      faxDataObj.action = CONSTANTS.DELETE;
      faxDataObj.phoneNumber = oldData.fax.replace('(', '').replace(')', '').replace('-', '');
      faxDataObj.phoneSeq = oldData.faxSeq;
    }

    if (!this.helperService.IsNullOrEmpty(faxDataObj.action)) {
      faxDataObj.phoneType = PHONE_TYPE.FAX;
      phoneList.push(faxDataObj);
    }

    return phoneList;
  }

  /**
   * Retrieve Contact Information for a ID(facility, system)
   * @param facilityId facility ID for whom contact has to be retrieved
   * @param idSeq idSeq to retrieve contact
   */
  async getContact(facilityId, idSeq?) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_CONTACT,
          params: {
            facilityId,
            idSeq,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const response = {
      isError: false,
      contactList: [],
      hasData: true,
    };
    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length > 0 && result.objects[0].json) {
        response.contactList = this.parseContact(JSON.parse(JSON.stringify(result.objects[0].json)));
      } else {
        response.hasData = false;
      }
    } else {
      response.isError = true;
    }

    return response;
  }

  /**
   * parse contact and construct model for view
   * @param respContactList contactlist received from API call
   */
  parseContact(respContactList) {
    const contactList = [];
    console.log(respContactList);
    for (let contact of respContactList) {
      console.log(contact);
      if (contact.identity) {


        const newContactObj = {
          contactSeq: contact.identity.idSeq,
          contactType: contact.contactType,
          contactTypeDescr: this.resourceManagerService.getLovColumnProperties(
            LOV_KEYS.CONTACT_TYPE,
            contact.contactType,
            LOV_PROPERTIES.DESCRIPTION
          ),
          displayOrder: this.resourceManagerService.getLovColumnProperties(
            LOV_KEYS.CONTACT_TYPE,
            contact.contactType,
            LOV_PROPERTIES.DISPLAY_ORDER
          ),
          lastName: contact.identity.lastName,
          firstName: contact.identity.firstName,
          middleName: contact.identity.middleName,
          contactName: `${contact.identity.lastName}, ${contact.identity.firstName} ${contact.identity.middleName}`,
          email: '',
          emailSeq: '',
          phone: '',
          phoneSeq: '',
          phoneExtn: '',
          fax: '',
          faxSeq: '',
        };

        if (contact.email) {
          newContactObj.email = contact.email.emailAddress;
          newContactObj.emailSeq = contact.email.emailSeq;
        }

        if (contact.phoneList) {
          for (const phone of contact.phoneList) {
            if (phone.phoneType === PHONE_TYPE.FAX) {
              newContactObj.fax = this.telephonePipe.transform(phone.phoneNumber);
              newContactObj.faxSeq = phone.phoneSeq;
            } else {
              newContactObj.phone = this.telephonePipe.transform(phone.phoneNumber);
              newContactObj.phoneSeq = phone.phoneSeq;
              newContactObj.phoneExtn = phone.phoneExtn;
            }
          }
        }

        contactList.push(JSON.parse(JSON.stringify(newContactObj)));
      }
    }


    return this.sortContactList(contactList);
  }

  /**
   * sort by ascending display order
   * @param contactList list of contacts
   */
  sortContactList(contactList) {
    return contactList.sort((a, b) => {
      const d1 = +a.displayOrder;
      const d2 = +b.displayOrder;
      return d1 > d2 ? 1 : d1 < d2 ? -1 : 0;
    });
  }

  /**
   * Adds, Updates or Deletes the contact information
   * @param action action ADD, UPDATE or DELETE
   * @param oldContactInfo existing Contact Information
   * @param contactInfo updated Contact Information
   * @param facilityId ID for whom contact is Added, Updated or Deleted
   * @param idSeq IDSeq for whom contact is Added, Updated or Deleted
   */
  async manageContact(action, oldContactInfo, contactInfo, facilityId, idSeq, contactDataList) {
    const request = {
      action,
      contactIdSeq: oldContactInfo.contactSeq ? oldContactInfo.contactSeq : '',
      contactType: contactInfo.contactType,
      contactTypeDescr: oldContactInfo.contactTypeDescr ? oldContactInfo.contactTypeDescr : '',
      firstName: contactInfo.firstName.toUpperCase(),
      lastName: contactInfo.lastName.toUpperCase(),
      middleName: contactInfo.middleName != null ? contactInfo.middleName.toUpperCase() : '',
      email: {
        action,
        emailType: EMAIL_TYPE.WORK,
        emailAddress: contactInfo.email,
        emailSeq: oldContactInfo.emailSeq ? oldContactInfo.emailSeq : '',
      },
      phoneList: [],
    } as any;

    if (!this.helperService.IsNullOrEmpty(facilityId)) {
      request.facilityId = facilityId;
    } else if (!this.helperService.IsNullOrEmpty(idSeq)) {
      request.idSeq = idSeq;
    }

    if (action === CONSTANTS.ADD || action === CONSTANTS.DELETE) {
      request.phoneList = this.formPhoneAndFaxRequest(contactInfo, action);
    } else if (action === CONSTANTS.UPDATE) {
      request.phoneList = this.compareAndFormPhoneAndFaxRequest(oldContactInfo, contactInfo);
    }
    if (action === CONSTANTS.ADD) {
      contactDataList.forEach((contact: any) => {
        if (contact.contactType == contactInfo.contactType) {
          this.helperService.showErrorMessage(
            'You already have a ' + contact.contactTypeDescr.toLowerCase() +
            ' contact! Please change the contact type and try adding again.'
          );
          return;
        }
      })
    };


    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_CONTACT, request));

    return result;
  }

  /**
   * Retrieve Address information
   * @param idSeq idSeq for which address will be obtained
   */
  async getAddress(idSeq, addressType?) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_ADDRESS,
          params: {
            idSeq,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const addressResult = {
      isError: false,
      address: {} as any,
      hasData: true,
    };

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length > 0 && result.objects[0].json && result.objects[0].json.length > 0) {
        addressResult.address = result.objects[0].json.filter(p=>p.addressType === addressType)[0];
      } else {
        addressResult.hasData = false;
      }
    } else {
      addressResult.isError = true;
    }

    return addressResult;
  }

  /**
   * Retrieve Address information
   * @param idSeq idSeq for which address will be obtained
   */
  async getAddressWithFacilityId(facilityId, addressType?) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_ADDRESS,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const addressResult = {
      isError: false,
      address: {} as any,
      hasData: true,
    };

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length > 0 && result.objects[0].json && result.objects[0].json.length > 0) {
        addressResult.address = result.objects[0].json.filter(p=>p.addressType === addressType)[0];
      } else {
        addressResult.hasData = false;
      }
    } else {
      addressResult.isError = true;
    }

    return addressResult;
  }

  /**
   * Manage Address
   * @param id id
   * @param idSeq idSeq
   * @param originalAddressInfo address Info before update
   * @param addressInfo adress Information updated
   */
  async manageAddress(id, idSeq, originalAddressInfo, addressInfo, comment?,addressType?) {
    const request = {
      action: originalAddressInfo.addressSeq ?CONSTANTS.UPDATE : CONSTANTS.ADD,
      facilityId: id,
      idSeq: "",
      addressSeq: originalAddressInfo.addressSeq,
      addressType: addressType,
      addressLine1: addressInfo.addressLine1.toUpperCase(),
      addressLine2: addressInfo.addressLine2.toUpperCase(),
      city: addressInfo.city.toUpperCase(),
      state: addressInfo.state,
      zip: addressInfo.zip,
      countyCode: addressInfo.countyCode ? addressInfo.countyCode : '0000',
      municipalityCode: addressInfo.municipalityCode ? addressInfo.municipalityCode : '0000',
      comment: comment
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_PROVIDER_ADDRESS, request));

    return result;
  }

  /**
   * Gets the interface entities based on STATUS
   * @param intfcEntityStatus status of the interface entity
   * @param reload to reload or use from MW cache
   */
  async searchInterfaceEntity(intfcEntityStatus, reload) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.SEARCH_INTERFACE_ENTITY,
          params: {
            intfcEntityStatus,
            reload,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_INTERFACE_BUNDLE, request));

    let entityResult;

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length && result.objects[0].json) {
        entityResult = result.objects[0].json;
      }
    }

    return entityResult;
  }

  /**
   * Creates or Updates Interface Profile for a facility
   * @param action ADD or UPDATE manageInterfaceProfile
   * @param facilityId facilityId for which Interface Profile has to be updated
   * @param origIntfcData original Interface Profile Data
   * @param newIntfcData modified Interface Profile Data
   * @param vendorName vendor name
   * @param facilityName facility name
   */
  async manageInterfaceProfile(action, facilityId, origIntfcData, newIntfcData, vendorName, facilityName) {
    const request = {
      action: action === CONSTANTS.ADD ? OPERATION.FACILITY.CREATE_INTERFACE_PROFILE : OPERATION.FACILITY.UPDATE_INTERFACE_PROFILE,
      intfcProfileMapSeq: action === CONSTANTS.UPDATE ? origIntfcData.intfcProfileMapSeq : '',
      facilityId,
      emailList: newIntfcData.emailTo.split(';'),
      vendorIdSeq: newIntfcData.vendor,
      vendorSoftware: newIntfcData.vendorSoftware,
      intfcType: newIntfcData.intfcType,
      intfcAccessType: newIntfcData.intfcAccessType,
      intfcEntityIdSeq: newIntfcData.entityName,
      webSvcAccountId: newIntfcData.webSvcAccountId,
      sftpAccountId: newIntfcData.sftpAccountName,
      intfcFormId: newIntfcData.intfcFormId,
      intfcEngine: newIntfcData.intfcEngine,
      fileType: newIntfcData.fileType,
      fileVersion: newIntfcData.fileVersion,
      intfcProfileStatus: newIntfcData.status,
      comment: this.helperService.encodeJsonString(newIntfcData.comments),
      vendorName,
      facilityName,
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_INTERFACE_PROFILE, request));

    return result;
  }

  /**
   * Gets users list for a facility
   * @param facilityId Facility Id for which users has to be retrieved
   * @param userType Optional parameter usertype for which users has to be retrieved
   */
  async getFacilityUsers(facilityId, userType?, coreFlag?:boolean ) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_FACILITY_USER,
          params: {
            facilityId,
            userType,
          },
        },
      ],
    };

    let result;

    if (coreFlag) {
      result = await lastValueFrom(this.apiService.post(CONFIG.GET_FACILITY_BUNDLE, request));

    } else {
      result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    }


    const facilityUsers = {
      isError: false,
      data: [],
      cssClassList: [],
    };

    if (result.status === CONSTANTS.SUCCESS || result.status === CONSTANTS.TRN_WARNING) {
      if (result.objects && result.objects.length > 0 && result.objects[0] && result.objects[0].json && result.objects[0].json.user) {
        facilityUsers.data = userType
          ? this.parseFacilityUsersList(result.objects[0].json.user)
          : this.excludeFacilityUsersList(result.objects[0].json.user);
        facilityUsers.data = facilityUsers.data.sort((a, b) => {
          if (a.lastName > b.lastName) {
            return 1;
          }
          if (a.lastName < b.lastName) {
            return -1;
          }
        });
        facilityUsers.cssClassList = this.formatUsersList(facilityUsers.data);
      }
    } else {
      facilityUsers.isError = true;
    }

    return facilityUsers;
  }

  /**
   * parse list of facility users, add full name
   * @param userList list of facility users
   */
  parseFacilityUsersList(userList) {
    userList.forEach((user) => {
      user.fullName = `${user.lastName} ${user.firstName}`;
    });

    return userList;
  }

  /**
   * parse list of facility users, remove users with profile VFC ADMIN, SUPER ADMIN, TRAINER, QA, CDS USER
   * @param userList list of facility users
   */
  excludeFacilityUsersList(userList) {
    return userList.filter((user) => {
      const firstSpaceIndex = user.profileName.indexOf(' ');

      const profileNameStartingWith = user.profileName.substr(0, firstSpaceIndex);

      if (!isNaN(profileNameStartingWith)) {
        user.profileName = user.profileName.substr(firstSpaceIndex + 1);
      }

      user.fullName = `${user.lastName} ${user.firstName}`;

      return this.excludedProfileList.indexOf(user.profileType) === -1;
    });
  }

  /**
   * for user with status of INACTIVE or REVOKED set class of text-danger
   * @param userList list of facility users
   */
  formatUsersList(userList) {
    const cssClassList = [];
    userList.forEach((user, index) => {
      for (const header of this.facilityUsersHeaders) {
        user.serialNum = index;
        if (user.status === 'INACTIVE' || user.status === 'REVOKED') {
          cssClassList[`'${user.serialNum}${header.field}'`] = 'text-danger';
        } else {
          cssClassList[`'${user.serialNum}${header.field}'`] = '';
        }
      }
    });
    return cssClassList;
  }

  /**
   * Get Facility Audit Note for a Facility
   * @param facilityId facilityId for which audit note is to be retrieved
   */
  async getFacilityNote(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.GLOBAL.GET_NOTE,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));

    const facilityNote = {
      isError: false,
      data: [],
    };

    if (this.helperService.isValidGetResult(result)) {
      facilityNote.data = this.parseFacilityNote(result.objects[0].json);
    } else {
      facilityNote.isError = true;
    }

    return facilityNote;
  }

    /**
   * Get Facility Audit Note for a Facility
   * @param facilityId facilityId for which audit note is to be retrieved
   */
    async getProviderCoordinator(facilityId) {
      const request = {
        objects: [
          {
            name: 'GET_PROVIDER_COORDINATOR',
            params: {
              facilityId,
            },
          },
        ],
      };
  
      const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));
  
      const coordinators = {
        isError: false,
        data: [],
      };
  
      if (this.helperService.isValidGetResult(result)) {
        coordinators.data = result.objects[0].json;
      } else {
        coordinators.isError = true;
      }
  
      return coordinators;
    }

  /**
   * Return noteList not having InterfaceProfileMapSeq and add VisibilityToList based on PROFILE_TYPE Lov,
   * also remove ADMIN PROFILE from VISIBILIT LIST
   * @param noteList audit Note List
   */
  parseFacilityNote(noteList) {
    const profileTypeSuperAdminVal = this.resourceManagerService.getLovColumnProperties(
      LOV_KEYS.PROFILE_TYPE,
      'Super Admin',
      'columnValue',
      'descr'
    );
    let index = 0;
    return noteList.filter((note) => {
      note.serialNum = index;
      note.comments = this.helperService.decodeJsonString(note.comments);
      note.origVisibilityToList = [];
      note.visibilityToList = [];
      note.visibilityToListDescr = '';
      note.profileList = note.noteBy = note.doneBy || note.userCreated;
      if (note.visibilityList) {
        for (const visibility of note.visibilityList) {
          if (visibility !== profileTypeSuperAdminVal) {
            const profile = this.resourceManagerService.getLovItem(LOV_KEYS.PROFILE_TYPE, visibility);
            if (profile) {
              note.visibilityToList.push(profile);
              note.origVisibilityToList.push(profile);
              if (!this.helperService.IsNullOrEmpty(note.visibilityToListDescr)) {
                note.visibilityToListDescr += `, ${profile.descr}`;
              } else {
                note.visibilityToListDescr = profile.descr;
              }
            }
          }
        }
      }
      index++;
      return this.helperService.IsNullOrEmpty(note.intfcProfileMapSeq);
    });
  }

  /**
   * Create Audit note
   * @param auditData audit Data
   * @param facilityId Facility Id
   */
  async createAuditNote(auditData, facilityId, isCoreAppRequired?:boolean) {
    const request = {
      action: CONSTANTS.ADD,
      doneBy: auditData.auditBy,
      facilityId,
      noteType: auditData.auditType,
      noteDate: this.helperService.formatDate(auditData.auditDate),
      vfcPin: auditData.vfcPin,
      visibilityList: !this.helperService.IsNullOrEmpty(auditData.auditVisibleTo)
        ? auditData.auditVisibleTo.map((audit) => audit.columnValue)
        : [],
      comments: this.helperService.encodeJsonString(auditData.comments),
    };

    return await this.manageAudit(request, isCoreAppRequired);
  }

  /**
   * update Audit Note
   * @param auditData auditData
   * @param facilityId facilityId
   */
  async updateAuditNote(auditData, facilityId,  isCoreAppRequired?:boolean) {
    const request = {
      action: CONSTANTS.UPDATE,
      noteSeq: auditData.noteSeq,
      facilityId,
      doneBy: auditData.noteBy,
      noteType: auditData.noteType,
      noteDate: this.helperService.formatDate(auditData.noteDate),
      vfcPin: auditData.vfcPin,
      visibilityList: auditData.visibilityToList.map((audit) => audit.columnValue),
      comments: this.helperService.encodeJsonString(auditData.comments),
    };
    return await this.manageAudit(request, isCoreAppRequired);
  }

  /**
   * add or update audit note
   * @param request request with add or update audit note
   */
  async manageAudit(request, isCoreAppRequired) {
    if(isCoreAppRequired){
      return await lastValueFrom(this.apiService.post(CONFIG.MANAGE_CORE_NOTE, request));

    }else {
      return await lastValueFrom(this.apiService.msPost(CONFIG.MANAGE_NOTE, request));

    }
  }

  /**
   * Reset WebSvcAccountId for a Facility
   * @param emails list of email separated by ';'
   * @param webSvcAccountId websvcAccountId
   */
  async resetWebserviceAccountKey(emails: string, webSvcAccountId: string) {
    const request = {
      webSvcAccountId,
      emailList: !this.helperService.IsNullOrEmpty(emails) ? emails.replace(/\s+/g, '').split(';') : '',
    };

    return await lastValueFrom(this.apiService.msPost(CONFIG.RESET_IMS_WEBSERVICE, request));
  }

  /**
   * Get VFC, VFC Inprogress for Facility
   * @param facilityId facilityId
   */
  async getVFCForFacility(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.VFC.GET_VFC,
          params: {
            facilityId,
          },
        },
        {
          name: OPERATION.VFC.GET_VFC_INPROGRESS,
          params: {
            facilityId,
          },
        },
      ],
    };

    const result = await lastValueFrom(this.apiService.post(CONFIG.GET_VFC_BUNDLE, request));

    const vfcList = {
      inProgressList: [],
      existingList: [],
      isError: false,
    };

    if (this.helperService.isValidGetResult(result)) {
      for (const vfcResult of result.objects) {
        if (vfcResult.name === OPERATION.VFC.GET_VFC_INPROGRESS) {
          vfcList.inProgressList = vfcResult.json || [];
        } else if (vfcResult.name === OPERATION.VFC.GET_VFC && vfcResult.json) {
          vfcList.existingList = vfcResult.json.vfc.json || [];
        }
      }
    } else {
      vfcList.isError = true;
    }

    return vfcList;
  }

  /**
   * get name of the facility
   * @param facilityId facilityId
   */
  async getFacilityName(facilityId) {
    const request = {
      objects: [
        {
          name: OPERATION.FACILITY.GET_FACILITY_NAME,
          params: {
            facilityId,
          },
        },
      ],
    };

    let facilityName = '';

    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    if (this.helperService.isValidGetResult(result) && result.objects.length > 0 && result.objects[0].json) {
      facilityName = result.objects[0].json.facilityName;
    }

    return facilityName;
  }

  /**
   * Search Facility
   * @param params search facility form page params
   * @param hideLoader indicate if loader has to be hidden
   */
  async searchFacilityForUser(params: any, hideLoader?: boolean) {
    const request = {
      objects: [],
    };

    const searchParams = {} as any;

    if (!this.helperService.IsNullOrEmpty(params.facilityId)) {
      searchParams.facilityId = params.facilityId;
    } else if (!this.helperService.IsNullOrEmpty(params.vfcPin)) {
      searchParams.vfcPin = params.vfcPin;
    } else if (!this.helperService.IsNullOrEmpty(params.facilityName)) {
      searchParams.facilityName = params.facilityName;
    }
    if (!this.helperService.IsNullOrEmpty(params.userName)) {
      searchParams.userName = params.userName;
    }
    request.objects.push({
      name: OPERATION.FACILITY.SEARCH_FACILITY,
      params: searchParams,
    });
    if (!hideLoader) {
      this.loadingService.displayLoader('Searching Facilities');
    }
    const results = await lastValueFrom(this.apiService.msPost(CONFIG.GET_MS_FACILITY_BUNDLE, request));

    this.loadingService.hideLoader();

    let facilitySearchResults = [];

    if (results && (results.status === CONSTANTS.SUCCESS || results.status === CONSTANTS.TRN_WARNING)) {
      if (results.objects && results.objects.length > 0 && results.objects[0].json) {
        facilitySearchResults = this.transformSearchFacilityResults(results.objects[0].json);
      }
    }

    return facilitySearchResults;
  }

  async getNote(type, facilityId) {
    let request: any = {};
    let params: any = {};
    params.facilityId = facilityId;
    // params.callerIdSeq = vm.user.userIdSeq;
    if (type === 'Facility_General') {
      params.noteType = "6";
    } else if (type === 'Facility_Mailing_Address') {
      params.noteType = "7";
    }  else if (type === 'Facility_Physical_Address') {
      params.noteType = "9";
    } else {
      params.noteType = "1";
    }
    request.objects = [{
      "name": "GET_NOTE",
      "params": params
    }];
    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_BUNDLE, request));
    return result;
  }

  async updateNote(origUser, addCommentFrmValue, facilityId, type) {
    const generalRequest = {
      idType: "Facility_General",
      facilityId: facilityId,
      comments: addCommentFrmValue.getRawValue().addcomment,
      userIdSeq: origUser.idSeq,
      noteType: '6',
      doneBy: origUser.userName,
      noteDate: this.helperService.formatDate(new Date()),
      action: "ADD"
    }
    const mailingRequest = {
      idType: "Facility_Mailing_Address",
      facilityId: facilityId,
      comments: addCommentFrmValue.getRawValue().addcomment,
      userIdSeq: origUser.idSeq,
      noteType: '7',
      doneBy: origUser.userName,
      noteDate: this.helperService.formatDate(new Date()),
      action: "ADD"
    }
    const physicalRequest = {
      idType: "Facility_Physical_Address",
      facilityId: facilityId,
      comments: addCommentFrmValue.getRawValue().addcomment,
      userIdSeq: origUser.idSeq,
      noteType: '9',
      doneBy: origUser.userName,
      noteDate: this.helperService.formatDate(new Date()),
      action: "ADD"
    }
    const interfaceRequest = {
      idType: "Facility_Interface",
      facilityId: facilityId,
      comments: addCommentFrmValue.getRawValue().addcomment,
      userIdSeq: origUser.idSeq,
      noteType: '1',
      doneBy: origUser.userName,
      noteDate: this.helperService.formatDate(new Date()),
      action: "ADD"
    }
    let request = type === "general" ? generalRequest : type === "address" ? mailingRequest : type === "phyAddress" ? physicalRequest : interfaceRequest;
    const result = await lastValueFrom(this.apiService.msPost(CONFIG.GET_GLOBAL_MANAGENOTE, request));
    if (result.status == "SUCCESS") {
      addCommentFrmValue.setFormAsPristineAndUnTouched;
      if (request.idType === "Facility_General") {
        const result = this.getNote('Facility_General', facilityId);
        addCommentFrmValue.value.addcomment = '';
      } else if (request.idType === "Facility_Mailing_Address") {
        //vm.model.address.comment = '';
        const result = this.getNote('Facility_Mailing_Address', facilityId);
        addCommentFrmValue.value.addcomment = '';
      } else if (request.idType === "Facility_Physical_Address") {
        //vm.model.address.comment = '';
        const result = this.getNote('Facility_Physical_Address', facilityId);
        addCommentFrmValue.value.addcomment = '';
      } else {
        const result = this.getNote('Facility_Interface', facilityId);
        addCommentFrmValue.value.addcomment = '';
      }
    } /*else {
        //this.helperService.showAlert("Error", response.message, "error");
        return [];
    }*/
    return result;
  }
}
